import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import {
  CasinoGamesProvider,
  CategoryGamesProvider,
  useApplicationState,
  useCasinoGamesState,
  useCasinoNewState,
} from '@atlas/core';
import { EmptyStateView, HeaderTitle, Icon, Loader } from '@atlas/ui';

import { useCasinoFilterState } from 'components/Pages/Casino/CasinoFilterContext';
import { MIN_SEARCH_COUNT } from 'components/Pages/Casino/Filter/FilterSearch';
import GameList from 'components/Pages/Casino/GameList/GameList';
import SearchCategoryGamesList from 'components/Pages/Casino/GameList/SearchCategoryGamesList';
import InfiniteScroller from 'components/Pages/Casino/InfiniteScroller';

import { GAME_PER_PAGE } from '../constants';
import LoadMoreButton from '../LoadMoreButton';

const DEFAULT_FILTER_VALUE = [];
const SEARCH_GAMES_COUNT = 10;
const searchSliderSettings = { rows: 1, size: 'M' };

const noop = () => {};

const GamesSearchResults = ({ activeItem, scrollParent, sliderSettings }) => {
  const [isEnableInfinityScroll, setIsEnableInfinityScroll] = useState(false);
  const { currentSlice, games, hasNext, loadMore, loaded, loading } = useCasinoGamesState();
  const { authenticated } = useApplicationState();
  const { identifyFavoriteGames } = useCasinoNewState();

  useEffect(() => {
    if (!authenticated || !games.length) return;

    const gameIds = games.map(({ id }) => id);

    identifyFavoriteGames(gameIds);
  }, [games, authenticated, identifyFavoriteGames]);

  const handleLoadMore = useCallback(() => {
    loadMore(currentSlice + 1);
  }, [currentSlice, loadMore]);

  useEffect(() => {
    setIsEnableInfinityScroll(false);
  }, []);

  const handleEnableInfinityScroll = useCallback(() => {
    handleLoadMore();
    setIsEnableInfinityScroll(true);
  }, [handleLoadMore]);

  const gameLength = games.length;
  const canRenderLoadMoreButton = useMemo(() => {
    return !isEnableInfinityScroll && !loading && gameLength >= GAME_PER_PAGE;
  }, [gameLength, isEnableInfinityScroll, loading]);

  const renderGamesSearchResults = useMemo(() => {
    if (loading && !games.length) {
      return (
        <div className="casino-search-state state-loading">
          <Loader height={48} width={48} />
          <EmptyStateView
            description="casino_search_loading_message"
            title="casino_search_loading_title"
          />
        </div>
      );
    }

    if (!games.length) {
      return (
        <>
          <div className="casino-search-state">
            <Icon className="error-modal__icon" name="not-found" />
            <EmptyStateView
              description="casino_search_no_results_message"
              title="casino_search_no_results_title"
            />
          </div>
          <SearchCategoryGamesList sliderSettings={searchSliderSettings} />
        </>
      );
    }

    return (
      <>
        <HeaderTitle className="sub-text" title="result_found_title" />
        <InfiniteScroller
          getScrollParent={scrollParent}
          hasMore={hasNext}
          isLoading={loading}
          isResolved={loaded}
          loadMore={isEnableInfinityScroll ? handleLoadMore : noop}
          useWindow={false}
        >
          <GameList
            activeItem={activeItem}
            games={games}
            hasLoadMoreButton={canRenderLoadMoreButton}
            isEnableInfinityScroll={isEnableInfinityScroll}
            isLoading={loading}
            isResolved={loaded}
            sliderSettings={sliderSettings}
          />
          <LoadMoreButton
            handleClick={handleEnableInfinityScroll}
            isShow={canRenderLoadMoreButton}
          />
        </InfiniteScroller>
      </>
    );
  }, [
    activeItem,
    canRenderLoadMoreButton,
    games,
    handleEnableInfinityScroll,
    handleLoadMore,
    hasNext,
    isEnableInfinityScroll,
    loaded,
    loading,
    scrollParent,
    sliderSettings,
  ]);

  return renderGamesSearchResults;
};

const SearchResults = ({
  activeItem,
  className,
  group,
  scrollParent,
  searchQuery,
  sliderSettings,
}) => {
  const { filter } = useCasinoFilterState();

  const renderSearchResults = useMemo(() => {
    if (searchQuery.length < MIN_SEARCH_COUNT) {
      return (
        <>
          {searchQuery.length > 0 && (
            <div className="casino-search-state">
              <Icon className="error-modal__icon" name="error" />
              <EmptyStateView
                description="casino_search_minimum_message"
                title="casino_search_minimum_title"
              />
            </div>
          )}
          <SearchCategoryGamesList sliderSettings={searchSliderSettings} />
        </>
      );
    }

    return (
      <CasinoGamesProvider
        categories={DEFAULT_FILTER_VALUE}
        group={group}
        name={filter.name}
        providers={DEFAULT_FILTER_VALUE}
      >
        <GamesSearchResults
          activeItem={activeItem}
          scrollParent={scrollParent}
          sliderSettings={sliderSettings}
        />
      </CasinoGamesProvider>
    );
  }, [activeItem, filter.name, group, scrollParent, searchQuery, sliderSettings]);

  return (
    <div className={cx('casino-search-results', className)}>
      <CategoryGamesProvider gamesCount={SEARCH_GAMES_COUNT} group={group}>
        {renderSearchResults}
      </CategoryGamesProvider>
    </div>
  );
};

export default SearchResults;
